import React from 'react';

import type { ReferentCampaignTemplateKey } from '../../../../@types/Company';
import { Variable } from '../../../common/Variable';

export type ModelId = 'model1' | 'model2' | 'model3' | 'model4' | 'model5';
export type VersionId = 'primary' | 'alternative' | 'custom';

export type Tab = {
  id: ModelId;
  title: string;
  badge: string;
  emoji: React.ReactNode;
  recommended?: boolean;
};

export type VersionMeta = {
  label: string;
  icon: React.ReactNode;
};

/** Built-in letter templates (primary/alternative). */
export const BUILTIN_MODEL_IDS: Exclude<ModelId, 'model5'>[] = ['model1', 'model2', 'model3', 'model4'];
export const MODEL_IDS: ModelId[] = [...BUILTIN_MODEL_IDS, 'model5'];
export const VERSION_ORDER: VersionId[] = ['primary', 'alternative'];

/**
 * Identifier of the "Modèle 5 — Personnalisé" custom-document model. Unlike
 * Models 1-4 which expose a primary/alternative pair, Model 5 represents a
 * single user-uploaded document per campaign.
 */
export const CUSTOM_MODEL_ID: ModelId = 'model5';
export const CUSTOM_VERSION_ID: VersionId = 'custom';
export const CUSTOM_TEMPLATE_KEY = 'model5-custom' as const;
/** Legacy key kept for reading older branding selections before migration. */
export const LEGACY_CUSTOM_TEMPLATE_KEY = 'model4-custom' as const;

export const isCustomModelId = (modelId: ModelId): modelId is 'model5' => modelId === CUSTOM_MODEL_ID;

/**
 * Versions available for each model. Models 1-4 expose a primary/alternative
 * pair; Model 5 only exposes a single "custom" entry tied to the uploaded
 * document.
 */
export const versionsForModel = (modelId: ModelId): VersionId[] => (
  isCustomModelId(modelId) ? [CUSTOM_VERSION_ID] : VERSION_ORDER
);

/**
 * Default body used to pre-fill the "Texte de l'email" textarea on the
 * Référent Mutation Location campaign. It is shown both on the Setup screen
 * (where it can be edited & saved) and on the Communication runtime tab
 * (where it serves as the copy-paste reminder for the email containing the
 * generated PDF as attachment). The PHP fixture mirrors this exact value so
 * dev / staging seeds match what production users see out of the box.
 */
export const DEFAULT_REFERENT_CAMPAIGN_EMAIL_TEXT = [
  'Bonjour,',
  '',
  'Dans le cadre de notre démarche de qualité, nous vous adressons ci-joint le document '
    + 'de présentation de votre référent mutation location.',
  '',
  'Ce document présente les coordonnées de votre référent ainsi que les services '
    + "qu'il est en mesure de vous apporter si vous avez un projet de vente ou de location.",
  '',
  "Nous restons à votre disposition pour tout complément d'information.",
  '',
  'Cordialement,',
].join('\n');

/**
 * Extracts the copy-paste text of a rendered model template (header + body).
 * The container must wrap a `[data-email-header]` and a `[data-email-body]`
 * element — every model rendered by `renderModelContent` honours that
 * contract. Returns an empty string when the container is empty.
 */
export const extractModelContainerText = (container: HTMLElement | null): string => {
  if (!container) { return ''; }
  const headerElement = container.querySelector<HTMLElement>('[data-email-header]');
  const bodyElement = container.querySelector<HTMLElement>('[data-email-body]');
  const headerText = headerElement?.textContent?.trim() ?? '';
  const bodyText = bodyElement?.innerText?.trim() ?? container.innerText.trim();
  return headerText ? `${headerText}\n\n${bodyText}` : bodyText;
};

/**
 * Writes the provided text to the clipboard, falling back to the legacy
 * `execCommand('copy')` flow when `navigator.clipboard` is unavailable
 * (older browsers, insecure contexts). Throws when both paths fail.
 */
export const writeTextToClipboard = async(text: string): Promise<void> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Clipboard unavailable in this environment');
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const successful = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!successful) {
    throw new Error('Fallback copy command failed');
  }
};

export const tabsConfig: Tab[] = [
  {
    id: 'model1',
    title: 'Présentation',
    badge: 'MODÈLE 1',
    emoji: <span role="img" aria-label="présentation" className="text-2xl leading-none">📨</span>,
  },
  {
    id: 'model2',
    title: 'Prise de contact',
    badge: 'MODÈLE 2',
    emoji: <span role="img" aria-label="prise de contact" className="text-2xl leading-none">🤝</span>,
  },
  {
    id: 'model3',
    title: 'Nouveau service',
    badge: 'MODÈLE 3',
    emoji: <span role="img" aria-label="nouveau service" className="text-2xl leading-none">🆕</span>,
    recommended: true,
  },
  {
    id: 'model4',
    title: 'Estimation',
    badge: 'MODÈLE 4',
    emoji: <span role="img" aria-label="estimation prix de vente" className="text-2xl leading-none">💰</span>,
  },
  {
    id: 'model5',
    title: 'Personnalisé',
    badge: 'MODÈLE 5',
    emoji: <span role="img" aria-label="document personnalisé" className="text-2xl leading-none">📎</span>,
  },
];

const standardVersionLabels: Partial<Record<VersionId, VersionMeta>> = {
  primary: {
    label: 'Version principale',
    icon: <span role="img" aria-label="version principale" className="text-lg leading-none">🗒️</span>,
  },
  alternative: {
    label: 'Version alternative',
    icon: <span role="img" aria-label="version alternative" className="text-lg leading-none">📝</span>,
  },
};

export const versionLabels: Record<ModelId, Partial<Record<VersionId, VersionMeta>>> = {
  model1: { ...standardVersionLabels },
  model2: { ...standardVersionLabels },
  model3: { ...standardVersionLabels },
  model4: { ...standardVersionLabels },
  model5: {
    custom: {
      label: 'Document personnalisé',
      icon: <span role="img" aria-label="document personnalisé" className="text-lg leading-none">📎</span>,
    },
  },
};

export const buildTemplateKey = (modelId: ModelId, versionId: VersionId): ReferentCampaignTemplateKey =>
  `${modelId}-${versionId}` as ReferentCampaignTemplateKey;

export const parseTemplateKey = (key: string | null | undefined): { modelId: ModelId; versionId: VersionId } | null => {
  if (typeof key !== 'string') {
    return null;
  }
  if (key === CUSTOM_TEMPLATE_KEY || key === LEGACY_CUSTOM_TEMPLATE_KEY) {
    return { modelId: CUSTOM_MODEL_ID, versionId: CUSTOM_VERSION_ID };
  }
  const match = key.match(/^(model[1-4])-(primary|alternative)$/);
  if (!match) {
    return null;
  }
  return { modelId: match[1] as ModelId, versionId: match[2] as VersionId };
};

/**
 * Optional metadata used when rendering the "Modèle 5 — Personnalisé"
 * placeholder card (the actual file content cannot be displayed inline; we
 * only surface the uploaded filename + download hint).
 */
export type CustomDocPreview = {
  filename?: string;
  downloadUrl?: string;
};

export const renderModelContent = (
  modelId: ModelId,
  version: VersionId,
  customDoc?: CustomDocPreview,
): JSX.Element | null => {
  const containerId = `${modelId}-${version}`;

  if (isCustomModelId(modelId)) {
    return (
      <div
        id={containerId}
        className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
      >
        <div
          data-email-header
          className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
        >
          Modèle 5 — Document personnalisé
        </div>
        <div data-email-body className="space-y-4 px-8 py-8 text-slate-600">
          {customDoc?.filename ? (
            <>
              <p>
                Vous avez importé un document personnalisé qui sera utilisé pour cette campagne :
              </p>
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-slate-700 shadow-sm">
                <p className="font-semibold break-all text-slate-900">
                  <span role="img" aria-label="pièce jointe" className="mr-2 align-middle">📎</span>
                  {customDoc.filename}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  L&apos;aperçu en ligne n&apos;est pas disponible pour les documents importés. Téléchargez le fichier pour le consulter.
                </p>
              </div>
            </>
          ) : (
            <p>
              Aucun document personnalisé n&apos;a encore été importé pour cette campagne.
              Rendez-vous dans <strong className="font-semibold text-slate-900">Paramétrage › Contenu croissance</strong> pour importer votre PDF / Word.
            </p>
          )}
        </div>
      </div>
    );
  }

  switch (modelId) {
    case 'model1':
      if (version === 'primary') {
        return (
          <div
            id={containerId}
            className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
          >
            <div
              data-email-header
              className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
            >
              Objet : Votre REFERENT MUTATION LOCATION dédié à vos projets de vente ou de location
            </div>
            <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
              <p>Chers copropriétaires,</p>
              <p>
                Afin de mieux vous accompagner dans vos démarches, le cabinet{' '}
                <Variable>[data-company-name]</Variable> a créé un poste de{' '}
                <strong className="font-semibold text-slate-900">« Référent Mutation Conseil »</strong>, occupé par{' '}
                <strong className="font-semibold text-slate-900">
                  <Variable>[data-referent-title] [data-referent-firstname] [data-referent-lastname]</Variable>
                </strong>.
              </p>
              <p>
                Son rôle est de vous informer et de vous guider lorsque vous envisagerez de{' '}
                <strong className="font-semibold text-slate-900">vendre ou de louer votre bien</strong>.
              </p>
              <p>
                Ces opérations impliquent en effet de transmettre à l'acquéreur, au notaire ou au futur locataire des informations que seul le syndic détient. Grâce à votre « Référent Mutation Conseil », vous pourrez{' '}
                <strong className="font-semibold text-slate-900">anticiper ces obligations et faciliter vos démarches</strong>.
              </p>
              <p>
                <strong className="font-semibold text-slate-900">Concrètement</strong>, <Variable>[data-referent-title] [data-referent-lastname]</Variable> est à votre disposition pour répondre à toutes vos questions administratives, techniques ou réglementaires liées à votre projet immobilier au sein de la copropriété.
              </p>
              <p>
                Parce que sa mission implique le traitement de données personnelles, <Variable>[data-referent-title] [data-referent-lastname]</Variable> a suivi une{' '}
                <strong className="font-semibold text-slate-900">formation RGPD</strong>. Vous bénéficiez ainsi d'un interlocuteur fiable et formé à la protection de vos données.
              </p>
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-slate-700 shadow-sm">
                <p>
                  <span role="img" aria-label="contact téléphonique" className="mr-2 align-middle">
                    📞
                  </span>
                  <strong className="font-semibold text-slate-900">
                    <Variable>[data-referent-firstname] [data-referent-lastname]</Variable>
                  </strong>
                </p>
                <p>Tél. : <Variable>[data-referent-phone]</Variable></p>
                <p>
                  <span role="img" aria-label="contact email" className="mr-2 align-middle">
                    ✉️
                  </span>
                  <Variable>[data-referent-email]</Variable>
                </p>
              </div>
              <div className="border-t border-slate-200 pt-6 text-slate-500">
                <p className="italic">Restant à votre disposition,</p>
                <p className="italic">Très cordialement,</p>
                <p className="font-semibold text-slate-700">
                  L'équipe <Variable>[data-company-name]</Variable>
                </p>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          id={containerId}
          className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
        >
          <div
            data-email-header
            className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
          >
            MODELE N°1 BIS -- PRESENTATION
          </div>
          <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
            <p>Chers copropriétaires,</p>
            <p>
              Pour assurer au mieux sa mission de syndic, le cabinet <Variable>[data-company-name]</Variable> a créé pour vous assister un poste de « Référent Mutation Conseil ».
            </p>
            <p>
              Il est occupé par <strong className="font-semibold text-slate-900"><Variable>[data-referent-title] [data-referent-firstname] [data-referent-lastname]</Variable></strong>.
            </p>
            <p>
              Son rôle est de vous fournir toute information lors, notamment, de la mise en location ou de la mise en vente de votre bien.
            </p>
            <p>
              Ces opérations nécessitent en effet de fournir à tout acquéreur et au notaire ou au locataire des informations que le syndic détient. Notre « Référent Mutation Conseil » vous permettra ainsi d'anticiper et de rendre ces obligations moins lourdes.
            </p>
            <div className="rounded-2xl border-l-4 border-amber-400 bg-amber-50 p-5 text-slate-700 shadow-sm">
              <p className="font-semibold tracking-wide text-amber-900 uppercase">En pratique :</p>
              <p className="mt-2">
                Il est à votre disposition pour vous informer et vous guider sur les obligations administratives, techniques, juridiques... dès que vous aurez un projet immobilier de vente ou de location, concernant un bien situé dans la copropriété.
              </p>
            </div>
            <p>
              La fonction du « Référent Mutation Conseil » nécessite l'utilisation de certaines données à caractère personnel des copropriétaires. C'est pourquoi <Variable>[data-referent-title] [data-referent-lastname]</Variable> a bénéficié d'une « formation RGPD ».
            </p>
            <p>N'hésitez pas à le contacter pour identifier vos obligations et anticiper les démarches à accomplir.</p>
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-slate-700 shadow-sm">
              <p>
                <strong className="font-semibold text-slate-900"><Variable>[data-referent-title] [data-referent-firstname] [data-referent-lastname]</Variable></strong>
              </p>
              <p>Téléphone : <Variable>[data-referent-phone]</Variable></p>
              <p>
                <span role="img" aria-label="contact email" className="mr-2 align-middle">
                  ✉️
                </span>
                <Variable>[data-referent-email]</Variable>
              </p>
            </div>
            <div className="border-t border-slate-200 pt-6 text-slate-500">
              <p className="italic">Restant à votre disposition,</p>
              <p className="italic">Très cordialement,</p>
              <p className="font-semibold text-slate-700">
                L'équipe <Variable>[data-company-name]</Variable>
              </p>
            </div>
          </div>
        </div>
      );

    case 'model2':
      if (version === 'primary') {
        return (
          <div
            id={containerId}
            className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
          >
            <div
              data-email-header
              className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
            >
              Objet : Vente ou location de votre bien ? Contactez votre Référent Mutation &amp; Location
            </div>
            <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
              <p>Chers Copropriétaires,</p>
              <p>
                Comme nous vous l'avons présenté dans nos précédentes communications,{' '}
                <strong className="font-semibold text-slate-900">
                  <Variable>[data-referent-title] [data-referent-firstname] [data-referent-lastname]</Variable>
                </strong>{' '}
                est désormais votre <strong className="font-semibold text-slate-900">Référent Mutation &amp; Location</strong> au sein du cabinet <Variable>[data-company-name]</Variable>.
              </p>
              <p>
                Son rôle est de <strong className="font-semibold text-slate-900">vous informer et de vous accompagner</strong> dans vos démarches liées à la <strong className="font-semibold text-slate-900">vente ou à la location</strong> de votre bien au sein de la copropriété :
              </p>
              <ul className="ml-5 list-disc space-y-2 text-slate-600">
                <li>Transmission des documents obligatoires aux acquéreurs, notaires ou locataires,</li>
                <li>Conseils pratiques sur les obligations administratives et techniques,</li>
                <li>Anticipation des formalités pour rendre vos démarches plus simples et plus rapides.</li>
              </ul>
              <p>
                Formé au RGPD, <Variable>[data-referent-title] [data-referent-lastname]</Variable> veille également à la{' '}
                <strong className="font-semibold text-slate-900">confidentialité et à la protection de vos données personnelles</strong>.
              </p>
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-slate-700 shadow-sm">
                <p>
                  <span role="img" aria-label="contact téléphonique" className="mr-2 align-middle">
                    📞
                  </span>
                  <strong className="font-semibold text-slate-900">
                    <Variable>[data-referent-firstname] [data-referent-lastname]</Variable>
                  </strong>
                </p>
                <p>Tél. : <Variable>[data-referent-phone]</Variable></p>
                <p>
                  <span role="img" aria-label="contact email" className="mr-2 align-middle">
                    ✉️
                  </span>
                  <Variable>[data-referent-email]</Variable>
                </p>
              </div>
              <div className="border-t border-slate-200 pt-6 text-slate-500">
                <p className="italic">Restant à votre disposition,</p>
                <p className="italic">Très cordialement,</p>
                <p className="font-semibold text-slate-700">
                  L'équipe <Variable>[data-company-name]</Variable>
                </p>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          id={containerId}
          className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
        >
          <div
            data-email-header
            className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
          >
            Objet : Contactez notre référent mutation location si vous avez un projet de vente ou de location de votre bien
          </div>
          <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
            <p>Chers Copropriétaires,</p>
            <p>
              Comme nous l'évoquons dans les newsletters que nous vous envoyons, <Variable>[data-referent-title] [data-referent-lastname]</Variable> est désormais au sein de notre cabinet votre « Référent Mutation Location Conseil ».
            </p>
            <div className="rounded-2xl border-l-4 border-amber-400 bg-amber-50 p-5 text-slate-700 shadow-sm">
              <p className="font-semibold tracking-wide text-amber-900 uppercase">En pratique :</p>
              <p className="mt-2">
                Il est à votre disposition pour vous informer et vous guider sur les obligations administratives, techniques... quand vous avez un projet immobilier de vente ou de location, concernant votre bien dans la copropriété.
              </p>
            </div>
            <p>
              Ces opérations nécessitent de fournir à l'acquéreur et au notaire, ou au locataire, des informations que le syndic détient. Notre « Référent Mutation Conseil » vous permettra ainsi d'anticiper et de rendre ces obligations moins lourdes.
            </p>
            <p>
              La fonction du « Référent Mutation Conseil » impliquant l'utilisation de données personnelles, <Variable>[data-referent-title] [data-referent-firstname] [data-referent-lastname]</Variable> a bénéficié d'une « formation RGPD ». Il saura ainsi protéger vos données personnelles.
            </p>
            <p>N'hésitez pas à prendre contact avec lui pour mieux comprendre vos obligations et les démarches à accomplir.</p>
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-slate-700 shadow-sm">
              <p>
                <strong className="font-semibold text-slate-900"><Variable>[data-referent-title] [data-referent-firstname] [data-referent-lastname]</Variable></strong>
              </p>
              <p>Téléphone : <Variable>[data-referent-phone]</Variable></p>
              <p>
                <span role="img" aria-label="contact email" className="mr-2 align-middle">
                  ✉️
                </span>
                <Variable>[data-referent-email]</Variable>
              </p>
            </div>
            <div className="border-t border-slate-200 pt-6 text-slate-500">
              <p className="italic">Restant à votre disposition,</p>
              <p className="italic">Très cordialement,</p>
              <p className="font-semibold text-slate-700">
                L'équipe <Variable>[data-company-name]</Variable>
              </p>
            </div>
          </div>
        </div>
      );

    case 'model3':
      if (version === 'primary') {
        return (
          <div
            id={containerId}
            className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
          >
            <div
              data-email-header
              className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
            >
              Objet : Nouveau service -- Obtenez gratuitement un rapport de présentation de votre copropriété
            </div>
            <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
              <p>Chers Copropriétaires,</p>
              <p>
                Lors d'une <strong className="font-semibold text-slate-900">vente</strong>, d'une{' '}
                <strong className="font-semibold text-slate-900">mise en location</strong> ou simplement pour mieux connaître la{' '}
                <strong className="font-semibold text-slate-900">valeur de votre bien</strong>, il est essentiel de disposer d'informations fiables et actualisées.
              </p>
              <p>
                Le cabinet <Variable>[data-company-name]</Variable> vous propose désormais,{' '}
                <strong className="font-semibold text-slate-900">gratuitement et sur simple demande</strong>, un{' '}
                <strong className="font-semibold text-slate-900">rapport de présentation détaillé de votre copropriété</strong> élaboré par votre « Référent Mutation &amp; Location ».
              </p>
              <p>Ce document comprend notamment :</p>
              <ul className="ml-5 list-disc space-y-2 text-slate-600">
                <li>
                  <span role="img" aria-label="check mark">✔</span>️ <strong className="font-semibold text-slate-900">Historique des mutations</strong> dans l'immeuble (au cours des 4 dernières années) : nombre de ventes, prix réalisés, tendances.
                </li>
                <li>
                  <span role="img" aria-label="check mark">✔</span>️ <strong className="font-semibold text-slate-900">Analyse du marché local</strong> dans un rayon de 150 mètres : prix constatés pour des biens similaires, afin de situer votre bien dans son contexte.
                </li>
                <li>
                  <span role="img" aria-label="check mark">✔</span>️ <strong className="font-semibold text-slate-900">Chiffres-clés du quartier</strong> et éléments utiles pour éclairer votre prise de décision.
                </li>
              </ul>
              <p>
                Pour obtenir ce rapport personnalisé, contactez directement votre{' '}
                <strong className="font-semibold text-slate-900">Référent Mutation &amp; Location certifié RGPD</strong>. Votre demande sera traitée en toute confidentialité.
              </p>
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-slate-700 shadow-sm">
                <p>
                  <span role="img" aria-label="contact téléphonique" className="mr-2 align-middle">
                    📞
                  </span>
                  <strong className="font-semibold text-slate-900">
                    <Variable>[data-referent-firstname] [data-referent-lastname]</Variable>
                  </strong>
                </p>
                <p>Tél. : <Variable>[data-referent-phone]</Variable></p>
                <p>
                  <span role="img" aria-label="contact email" className="mr-2 align-middle">
                    ✉️
                  </span>
                  <Variable>[data-referent-email]</Variable>
                </p>
              </div>
              <div className="border-t border-slate-200 pt-6 text-slate-500">
                <p className="italic">Restant à votre disposition,</p>
                <p className="italic">Très cordialement,</p>
                <p className="font-semibold text-slate-700">
                  L'équipe <Variable>[data-company-name]</Variable>
                </p>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          id={containerId}
          className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
        >
          <div
            data-email-header
            className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
          >
            NOUVEAU SERVICE : Obtenez un rapport de présentation de votre copropriété grâce à votre Référent Mutation &amp; Location
          </div>
          <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
            <p>
              Lors d'une <strong className="font-semibold text-slate-900">vente</strong>, d'une{' '}
              <strong className="font-semibold text-slate-900">mise en location</strong> ou simplement pour connaître la valeur de votre bien, il convient de disposer d'informations{' '}
              <strong className="font-semibold text-slate-900">claires et objectives sur la situation de l'immeuble</strong> et de son environnement immédiat.
            </p>
            <p>
              Notre cabinet vous propose gratuitement un <strong className="font-semibold text-slate-900">rapport de présentation détaillé</strong> de votre copropriété, remis sur simple demande. Ce document est conçu pour vous donner tous les repères utiles pour sécuriser votre projet :
            </p>
            <ul className="ml-5 list-disc space-y-2 text-slate-600">
              <li>
                <span role="img" aria-label="check mark">✔</span>️ <strong className="font-semibold text-slate-900">Historique des mutations dans l'immeuble</strong> : combien de ventes réalisées au cours des 4 dernières années ? À quels prix ? Quelle est la tendance des prix ?
              </li>
              <li>
                <span role="img" aria-label="check mark">✔</span>️ <strong className="font-semibold text-slate-900">Analyse de la dynamique du quartier</strong> : prix de vente constatés pour des biens similaires dans un rayon de 150 mètres autour de votre immeuble, pour situer votre lot dans son contexte local.
              </li>
              <li>
                <span role="img" aria-label="check mark">✔</span>️ <strong className="font-semibold text-slate-900">Chiffres-clés du quartier…</strong>
              </li>
            </ul>
            <p>Cette synthèse vous offre une <strong className="font-semibold text-slate-900">base factuelle</strong> pour préparer un éventuel projet.</p>
            <p>
              <strong className="font-semibold text-slate-900">Comment l'obtenir ?</strong> Contactez directement votre <strong className="font-semibold text-slate-900">Référent Mutation &amp; Location certifié RGPD</strong> : il traitera votre demande en toute confidentialité et vous remettra un rapport à jour, clair et compréhensible.
            </p>
            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-5 text-slate-700 shadow-sm">
              <p>
                <span role="img" aria-label="contact téléphonique" className="mr-2 align-middle">
                  📞
                </span>
                <strong className="font-semibold text-slate-900">
                  <Variable>[data-referent-firstname] [data-referent-lastname]</Variable>
                </strong>
              </p>
              <p>Tél. : <Variable>[data-referent-phone]</Variable></p>
              <p>
                <span role="img" aria-label="contact email" className="mr-2 align-middle">
                  ✉️
                </span>
                <Variable>[data-referent-email]</Variable>
              </p>
            </div>
            <div className="border-t border-slate-200 pt-6 text-slate-500">
              <p className="italic">Restant à votre disposition,</p>
              <p className="italic">Très cordialement,</p>
              <p className="font-semibold text-slate-700">
                L'équipe <Variable>[data-company-name]</Variable>
              </p>
            </div>
          </div>
        </div>
      );

    case 'model4':
      if (version === 'primary') {
        return (
          <div
            id={containerId}
            className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
          >
            <div
              data-email-header
              className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
            >
              Objet : Vendre votre appartement : bien estimer le prix en tenant compte de l&apos;état daté de votre bien
            </div>
            <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
              <p>Madame, Monsieur,</p>
              <p>
                En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d&apos;un lot. À ce titre, je souhaite attirer votre attention sur un point souvent méconnu lors de la mise en vente d&apos;un appartement en copropriété.
              </p>
              <p>
                À l&apos;occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l&apos;acquéreur avant la signature de l&apos;acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.
              </p>
              <p>
                Si l&apos;acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu&apos;il n&apos;avait pas anticipés, la vente peut s&apos;en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.
              </p>
              <p>
                C&apos;est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.
              </p>
              <p>
                En tant que « Référent Mutation Location », mon rôle est de vous informer au plus près de votre copropriété en vous envoyant un rapport sur votre immeuble. Vous y trouverez notamment les premiers éléments concernant le marché immobilier local, les ventes réalisées dans un rayon de 150 mètres autour de l&apos;immeuble au cours des quatre dernières années (extrait du fichier impots.gouv) et d&apos;autres informations utiles.
              </p>
              <p>
                À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.
              </p>
              <p>
                Si vous souhaitez ensuite aller plus loin — ou si vous disposez déjà d&apos;une estimation et souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble et à votre bien — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d&apos;une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.
              </p>
              <p>
                Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l&apos;estimation que si vous en faites vous-même la demande.
              </p>
              <p>
                Pour toute question, ou pour recevoir le rapport de votre immeuble, vous pouvez répondre à ce message ou me joindre au{' '}
                <Variable>[data-referent-phone]</Variable> ou par mail à <Variable>[data-referent-email]</Variable>.
              </p>
              <div className="border-t border-slate-200 pt-6 text-slate-500">
                <p>Je reste à votre disposition,</p>
                <p className="font-semibold text-slate-700">
                  <Variable>[data-referent-firstname] [data-referent-lastname]</Variable>
                </p>
                <p>Référent mutation-location</p>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          id={containerId}
          className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
        >
          <div
            data-email-header
            className="border-b border-slate-100 bg-slate-50 px-8 py-6 text-lg font-semibold text-slate-900"
          >
            Objet : Vendre votre appartement : comment estimer le prix de votre bien avant une mise en vente ?
          </div>
          <div data-email-body className="space-y-5 px-8 py-8 text-slate-600">
            <p>Cher copropriétaire,</p>
            <p>
              En tant que référent mutation-location au sein de votre syndic, je suis votre interlocuteur dédié pour toutes les questions liées à la vente ou à la location d&apos;un lot. À ce titre, je souhaite attirer votre attention sur les points souvent méconnus lors de la mise en vente d&apos;un appartement en copropriété.
            </p>
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-700 shadow-sm">
                <p className="font-semibold text-slate-900">1 / Les diagnostics</p>
                <p className="mt-2">
                  Ils sont obligatoires pour la mise en vente. S&apos;ils sont purement informatifs, ils peuvent devenir des points bloquants pour votre acquéreur. Notamment le DPE qui renseigne sur l&apos;état énergétique de votre bien. Il est donc impératif de bien les interpréter pour estimer un bien.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-700 shadow-sm">
                <p className="font-semibold text-slate-900">2 / L&apos;état daté</p>
                <p className="mt-2">
                  À l&apos;occasion de toute vente, le notaire demande au syndic un document appelé « état daté ». Ce document, remis à l&apos;acquéreur avant la signature de l&apos;acte définitif, récapitule notamment : les charges courantes et le budget de la copropriété, les sommes restant dues au syndicat, les travaux votés et à venir — dont ceux inscrits au plan pluriannuel de travaux (PPPT) — ainsi que les éventuelles procédures en cours.
                </p>
                <p className="mt-2">
                  C&apos;est pourquoi une estimation réalisée sans connaissance précise de la copropriété (PPPT, travaux votés, budget, procédures) peut se révéler éloignée du prix réellement réalisable.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-700 shadow-sm">
                <p className="font-semibold text-slate-900">3 / Les travaux de copropriété</p>
                <p className="mt-2">
                  Si l&apos;acquéreur découvre à ce stade des travaux votés ou un niveau de charges qu&apos;il n&apos;avait pas anticipés, la vente peut s&apos;en trouver fragilisée : renégociation du prix convenu, retard, voire remise en cause de la transaction.
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-700 shadow-sm">
                <p className="font-semibold text-slate-900">4 / Les prix de vente dans votre immeuble et alentours</p>
                <p className="mt-2">
                  Dans un marché en évolution, il est impératif de bien maîtriser les valeurs de ventes réalisées dans un rayon proche. Le prix de mise en vente doit se situer dans la valeur marché pour éviter une perte de l&apos;intérêt commercial de votre bien.
                </p>
              </div>
            </div>
            <p>
              Pour vous permettre d&apos;y voir plus clair, je peux, dans un premier temps et sur simple demande, vous adresser le rapport de copropriété de votre immeuble. Vous y trouverez déjà des éléments intéressants sur le marché immobilier : les ventes réalisées dans un rayon de 150 mètres autour de l&apos;immeuble au cours des quatre dernières années.
            </p>
            <p>
              À la suite de cette réception, je pourrai, si vous le souhaitez, échanger avec vous pour vous commenter ce document et répondre à vos questions si vous avez un projet de vente.
            </p>
            <p>
              Si vous disposez déjà d&apos;une estimation mais souhaitez en obtenir une seconde, complémentaire, intégrant cette fois les informations propres à votre immeuble — je pourrai, après notre échange, si vous le souhaitez, convenir avec vous de la visite d&apos;une personne spécialisée du cabinet, qui se mettra en relation avec votre gestionnaire pour réaliser cette estimation en tenant compte de la vie de votre immeuble.
            </p>
            <p>
              Cette démarche est entièrement facultative et sans engagement : vos coordonnées ne seront communiquées à la personne en charge de l&apos;estimation que si vous en faites vous-même la demande.
            </p>
            <p>
              Pour toute question, ou pour recevoir le rapport de votre immeuble, vous pouvez répondre à ce message ou me joindre au{' '}
              <Variable>[data-referent-phone]</Variable> ou par mail à <Variable>[data-referent-email]</Variable>.
            </p>
            <div className="border-t border-slate-200 pt-6 text-slate-500">
              <p>Je reste à votre disposition,</p>
              <p className="font-semibold text-slate-700">
                <Variable>[data-referent-firstname] [data-referent-lastname]</Variable>
              </p>
              <p>Référent mutation-location</p>
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};
